import { HAS_WINDOW_LISTENERS } from '@eventbrite/feature-detection';
import { FunctionComponent, useEffect } from 'react';
import {
    BASE_URL,
    CHECKOUT_PATH,
    DELIVERY_METHODS_PATH,
    PRE_ORDER_CONFIRMATION_ORGANIZER_QUESTIONS_PATH,
    RESERVED_TICKETS_CONFIRMATION_PATH,
    TICKETS_COMPACT_SIZE_PATH,
} from '../../constants';

interface LeaveCheckoutWarningPropsBase {
    abandonOrderOnClose: () => void;
    isAddAttendeesOrder: boolean;
    isTransferredOrder: boolean;
    performTabless: boolean;
    routePathname: string;
}

// Prevent passing true to isAddAttendeesOrder and isTransferredOrder at the same time
export type LeaveCheckoutWarningProps = LeaveCheckoutWarningPropsBase &
    (
        | {
              isAddAttendeesOrder: boolean;
              isTransferredOrder: false;
          }
        | {
              isAddAttendeesOrder: false;
              isTransferredOrder: boolean;
          }
    );

const shouldShowLeaveWarningByPage: Record<string, boolean> = {
    [CHECKOUT_PATH]: true,
    [DELIVERY_METHODS_PATH]: true,
    [PRE_ORDER_CONFIRMATION_ORGANIZER_QUESTIONS_PATH]: true,
    [RESERVED_TICKETS_CONFIRMATION_PATH]: true,
    [TICKETS_COMPACT_SIZE_PATH]: false,
};

export const LeaveCheckoutWarning: FunctionComponent<
    LeaveCheckoutWarningProps
> = ({
    abandonOrderOnClose,
    isAddAttendeesOrder,
    isTransferredOrder,
    performTabless,
    routePathname,
}) => {
    const shouldShowLeaveWarningInPage =
        shouldShowLeaveWarningByPage[
            routePathname.replace(`${BASE_URL}/`, '')
        ] || false;

    const shouldShowLeaveWarning =
        shouldShowLeaveWarningInPage &&
        !isAddAttendeesOrder &&
        !isTransferredOrder &&
        !performTabless;

    useEffect(() => {
        if (!HAS_WINDOW_LISTENERS || !shouldShowLeaveWarning) {
            return;
        }

        const _handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };

        const _handleUnload = async () => {
            abandonOrderOnClose();
        };

        window.addEventListener('beforeunload', _handleBeforeUnload);
        window.addEventListener('unload', _handleUnload);

        return () => {
            window.removeEventListener('beforeunload', _handleBeforeUnload);
            window.removeEventListener('unload', _handleUnload);
        };
    }, [abandonOrderOnClose, shouldShowLeaveWarning]);

    return null;
};
